import React from 'react';
import PropTypes from 'prop-types';
import Img from 'gatsby-image';
import styled from 'styled-components';

import { breakpoints } from '../constants/mediaqueries';
import { ROOM_FEATURED_IMAGE_ASPECT_RATIO } from '../constants/sizes';
import { updateFluidSrcSetData } from '../helpers/image';


// Pointer travel (px) before a mousedown counts as a drag rather than a click.
const DRAG_THRESHOLD = 3;
// Release fling: each frame keeps a fraction of the pointer's last velocity so
// the track glides to rest instead of stopping dead when the mouse lets go,
// echoing the momentum of mobile's native touch scroll. Friction is tuned to
// settle within about a second; below the min velocity we stop the loop.
const MOMENTUM_FRICTION = 0.94;
const MOMENTUM_MIN_VELOCITY = 0.4;


const Frame = styled.div`
  margin: 1.5rem 0 0;

  /* Wide two-column layout (xl and up): a positioned box with a definite,
     viewport-scaled height rather than one stretched to the copy. The copy's
     height swings with column width, so matching it left the media either
     overgrown (shoving the layout) or starved. A fixed clamp gives the
     absolutely-filled track a stable height to scale to. Kept fairly shallow so
     the media reads as a landscape strip, not a tall panel, on big monitors.
     Below xl the gallery stacks under the copy (see the base rules) so narrower
     laptops and iPad portrait, where a side column would go near-square, get the
     natural full-width treatment instead. */
  @media ${breakpoints.xl} {
    position: relative;
    height: clamp(18rem, 20vw, 26rem);
    margin: 0;
  }
`;

/* One lateral track for every breakpoint. Slides are narrower than the track so
   the next image always peeks in past the right edge, inviting a sideways
   scroll.

   The scrollbar is painted as a faint, thin thumb at every breakpoint so any
   bar the browser actually renders (notably a desktop window, including one
   narrowed to phone width in responsive testing) reads as subtle rather than
   the stark default. Real touch devices draw their own overlay scrollbar that
   fades out and ignore these rules, so mobile keeps its native behaviour.

   Snapping is dropped for the whole mouse gesture, drag and release fling
   alike: driving scrollLeft by hand fights scroll-snap, so with it on the track
   stutters mid-drag and lurches to the nearest slide as the fling settles. That
   held at every width a mouse can reach, including a narrowed desktop window, so
   the suppression is keyed to the JS state rather than the breakpoint. Real
   touch keeps proximity snap at rest, since it never triggers the drag handler.
   The xl two-column view drops snap outright: it is mouse-only and reads better
   as a free-scrolling strip. */
const Track = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: ${(props) => (props.isDragging || props.isGliding ? 'none' : 'x proximity')};
  -webkit-overflow-scrolling: touch;
  cursor: ${(props) => (props.isDragging ? 'grabbing' : 'grab')};
  user-select: ${(props) => (props.isDragging ? 'none' : 'auto')};
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.25);
    border-radius: 2px;
  }

  img {
    -webkit-user-drag: none;
    user-select: none;
  }

  @media ${breakpoints.xl} {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    scroll-snap-type: none;
  }
`;

/* Mobile: 82% width with a fixed aspect ratio leaves ~18% of the next slide
   peeking in, height following from width. Desktop: the slide fills the track's
   height (the intro text height) and takes a definite 90% width so the media
   scales up while the next slide still peeks past the column. */
const Slide = styled.div`
  position: relative;
  flex: 0 0 82%;
  aspect-ratio: ${ROOM_FEATURED_IMAGE_ASPECT_RATIO};
  overflow: hidden;
  scroll-snap-align: start;

  @media ${breakpoints.xl} {
    flex: 0 0 90%;
    height: 100%;
    aspect-ratio: auto;
  }
`;

/* The lead facade slide is a plain CMS asset URL, not a Gatsby fluid image, so
   it renders as a raw img rather than through gatsby-image. Cover to fill the
   slide the same way the fluid slides do. */
const PlainImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DirectBookingGallery = ({ ariaLabel, images }) => {
  const trackRef = React.useRef(null);
  const drag = React.useRef({
    active: false, startX: 0, scrollLeft: 0, lastX: 0, velocity: 0,
  });
  const momentumFrame = React.useRef(null);
  const [isDragging, setIsDragging] = React.useState(false);
  // Tracks the release fling so scroll-snap stays suppressed until the glide
  // fully settles; re-snapping mid-fling is what made narrow windows lurch.
  const [isGliding, setIsGliding] = React.useState(false);

  const stopMomentum = () => {
    if (momentumFrame.current) window.cancelAnimationFrame(momentumFrame.current);
    momentumFrame.current = null;
    setIsGliding(false);
  };

  const glide = () => {
    const node = trackRef.current;
    if (!node) return;
    node.scrollLeft -= drag.current.velocity;
    drag.current.velocity *= MOMENTUM_FRICTION;
    if (Math.abs(drag.current.velocity) > MOMENTUM_MIN_VELOCITY) {
      momentumFrame.current = window.requestAnimationFrame(glide);
    } else {
      momentumFrame.current = null;
      setIsGliding(false);
    }
  };

  React.useEffect(() => () => {
    if (momentumFrame.current) window.cancelAnimationFrame(momentumFrame.current);
  }, []);

  const beginDrag = (event) => {
    const node = trackRef.current;
    if (!node) return;
    // A fresh grab cancels any glide still running from the previous fling.
    stopMomentum();
    drag.current = {
      active: true, startX: event.pageX, scrollLeft: node.scrollLeft, lastX: event.pageX, velocity: 0,
    };
  };

  const moveDrag = (event) => {
    const node = trackRef.current;
    if (!node || !drag.current.active) return;
    const delta = event.pageX - drag.current.startX;
    // Arm the drag proper only once the pointer has actually travelled, so a
    // plain click doesn't flip the grabbing cursor or fling on release.
    if (!isDragging && Math.abs(delta) > DRAG_THRESHOLD) setIsDragging(true);
    // Frame-to-frame pointer travel drives the release fling.
    drag.current.velocity = event.pageX - drag.current.lastX;
    drag.current.lastX = event.pageX;
    node.scrollLeft = drag.current.scrollLeft - delta;
  };

  const endDrag = () => {
    if (!drag.current.active) return;
    drag.current.active = false;
    setIsDragging(false);
    if (Math.abs(drag.current.velocity) > MOMENTUM_MIN_VELOCITY) {
      setIsGliding(true);
      momentumFrame.current = window.requestAnimationFrame(glide);
    }
  };

  if (!images.length) return null;

  return (
    <Frame>
      <Track
        aria-label={ariaLabel}
        innerRef={trackRef}
        isDragging={isDragging}
        isGliding={isGliding}
        onMouseDown={beginDrag}
        onMouseLeave={endDrag}
        onMouseMove={moveDrag}
        onMouseUp={endDrag}
        role="group"
      >
        <For each="image" index="index" of={images}>
          <Slide key={image.id}>
            <If condition={image.fluid}>
              <Img
                alt={image.alt || ''}
                fluid={updateFluidSrcSetData(image.fluid)}
                imgStyle={{ objectFit: 'cover' }}
                loading={index === 0 ? 'eager' : 'lazy'}
                style={{ height: '100%' }}
              />
            </If>
            <If condition={!image.fluid}>
              <PlainImage
                alt={image.alt || ''}
                loading={index === 0 ? 'eager' : 'lazy'}
                src={image.src}
              />
            </If>
          </Slide>
        </For>
      </Track>
    </Frame>
  );
};

DirectBookingGallery.propTypes = {
  ariaLabel: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.shape({
    alt: PropTypes.string,
    fluid: PropTypes.object,
    id: PropTypes.string,
    src: PropTypes.string,
  })).isRequired,
};

DirectBookingGallery.defaultProps = {
  ariaLabel: 'Michelberger impressions',
};

export default DirectBookingGallery;
